import { ChangeEvent, Dispatch, SetStateAction } from "react";
import Select, { SelectChangeEvent, SelectProps } from "@mui/material/Select";
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
} from "@mui/material";

import { useProxyProjects } from "../../hooks/proxy/projects";
import {
  KeyGenAlgorithm,
  KeyType,
  KeyWrapAlgorithm,
  useProxyKeys,
} from "../../hooks/proxy/keys";

export type KeyAlgorithm = KeyGenAlgorithm & KeyWrapAlgorithm;

export const SelectKeyType = ({
  type,
  disabled,
  setType,
}: {
  type: KeyType;
  disabled: boolean;
  setType: Dispatch<SetStateAction<KeyType>>;
}) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) =>
    setType(e.target.value as KeyType);

  return (
    <FormControl>
      <RadioGroup
        row
        value={type}
        onChange={handleChange}
        aria-labelledby="upload-key-type"
      >
        {["wrapped", "component", "p12"].map((kType) => (
          <FormControlLabel
            key={kType}
            value={kType}
            label={kType}
            disabled={disabled}
            control={<Radio size="small" sx={{ py: 0 }} />}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

export const SelectProject = ({
  size,
  value,
  onChange,
  disabled,
  required,
  returnValue,
}: {
  size?: SelectProps["size"];
  value: string;
  required?: boolean;
  disabled?: boolean;
  onChange: (e: string) => void;
  returnValue: "id" | "name";
}) => {
  const projects = useProxyProjects();

  const handleChange = (event: SelectChangeEvent) =>
    onChange(event.target.value as string);

  return (
    <FormControl fullWidth>
      <InputLabel
        id="project-select-label"
        size={size === "small" ? "small" : "normal"}
        required={required}
      >
        Project
      </InputLabel>

      <Select
        id="project-select"
        size={size}
        value={value}
        disabled={disabled}
        required={required}
        label="Project"
        labelId="project-select-label"
        onChange={handleChange}
      >
        {projects.data?.data.length === 0 ? (
          <MenuItem disabled key={"no-project-found"} value={0}>
            No projects found
          </MenuItem>
        ) : (
          projects.data?.data.map(({ id, name }) => (
            <MenuItem value={returnValue === "id" ? id : name}>{name}</MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
};

export const SelectAlgorithm = ({
  label = "Algorithm",
  value,
  onChange,
  disabled,
  required,
  algorithms,
}: {
  value: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  onChange: (e: KeyAlgorithm) => void;
  algorithms: { key: string; hidden: boolean; disabled: boolean }[];
}) => {
  const handleChange = (event: SelectChangeEvent) =>
    onChange(event.target.value as KeyAlgorithm);

  return (
    <FormControl fullWidth>
      <InputLabel required={required} id="algorithm-select-label">
        {label}
      </InputLabel>

      <Select
        labelId="algorithm-select-label"
        id="algorithm-select"
        label={label}
        value={value}
        disabled={disabled}
        onChange={handleChange}
      >
        {algorithms.map(({ key, hidden, disabled }) => (
          <MenuItem
            key={key}
            value={key}
            disabled={disabled}
            sx={{ display: hidden ? "none" : "inherit" }}
          >
            {key.replaceAll("_", " ")}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const SelectWrappingKey = ({
  value,
  disabled,
  required,
  onChange,
  projectId,
}: {
  value: string;
  disabled: boolean;
  required?: boolean;
  projectId: string;
  onChange: (e: string) => void;
}) => {
  const keys = useProxyKeys({
    projectId,
    type: "wrapping",
    status: "issued",
  });

  const handleChange = (event: SelectChangeEvent) =>
    onChange(event.target.value as string);

  return (
    <FormControl fullWidth>
      <InputLabel id="wrapping-key-select-label" required={required}>
        Wrapping key
      </InputLabel>
      <Select
        id="wrapping-key-select"
        labelId="wrapping-key-select-label"
        label="Wrapping key"
        value={value}
        required={required}
        disabled={disabled || !projectId}
        onChange={handleChange}
      >
        {keys.data?.data.length === 0 ? (
          <MenuItem disabled key={"no-key-found"} value={0}>
            No keys found
          </MenuItem>
        ) : (
          keys.data?.data.map((key) => (
            <MenuItem key={key.id} value={key.id}>
              {key.name}
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
};

export const SelectTotalComponents = ({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled: boolean;
  onChange: (e: string) => void;
}) => {
  const handleChange = (event: SelectChangeEvent) =>
    onChange(event.target.value as string);

  return (
    <FormControl fullWidth>
      <InputLabel required id="component-total-select">
        Key components
      </InputLabel>

      <Select
        id="component-total-select"
        labelId="component-total-select"
        label="Key components"
        value={value}
        disabled={disabled}
        onChange={handleChange}
      >
        {["1", "2", "3", "4", "5"].map((total) => (
          <MenuItem key={total} value={total}>
            {total}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
